import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setAmazonStore } from "@/lib/searchLinks";
import { setLibMode } from "@/lib/api";
import { resetPalettes } from "@/lib/palettes";
import { resetRatings } from "@/lib/ratings";
import { pullPrefs, setSyncActive } from "@/lib/prefsSync";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const setUser = useCallback((u) => {
    if (u) { setAmazonStore(u.amazon_store); if (!u.library_share) setLibMode("mine"); localStorage.setItem("rh.user", JSON.stringify(u)); }
    else localStorage.removeItem("rh.user");
    resetPalettes();
    resetRatings();
    setSyncActive(!!u && !u.offline);
    setUserState(u);
  }, []);

  useEffect(() => {
    // Pick up settings changed on another device when this tab comes back after a while.
    let hiddenAt = 0;
    const onVis = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) pullPrefs().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    // Offline (no response at all): keep the last signed-in user so saved music stays reachable.
    api.get("/auth/me").then((r) => setUser(r.data)).catch((e) => {
      const cached = !e.response && localStorage.getItem("rh.user");
      setUser(cached ? { ...JSON.parse(cached), offline: true } : false);
    });
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [setUser]);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => api.post("/auth/refresh").catch(() => {}), 20 * 60 * 1000);
    return () => clearInterval(t);
  }, [user]);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, [setUser]);

  const login = useCallback(async (email, password, turnstileToken = "") => {
    const { data } = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken });
    if (data.mfa_required) return data;
    await refreshUser();
    return data;
  }, [refreshUser]);

  const register = useCallback(async (name, email, password, invite = "", turnstileToken = "") => {
    const { data } = await api.post("/auth/register", { name, email, password, invite, turnstile_token: turnstileToken });
    if (data.pending_approval) return data;
    await refreshUser();
    return data;
  }, [refreshUser]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, [setUser]);

  const updatePrefs = useCallback(async (prefs) => {
    const { data } = await api.put("/auth/prefs", prefs);
    setUser(data);
  }, [setUser]);

  return <AuthCtx.Provider value={{ user, login, register, logout, updatePrefs, refreshUser }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
